import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../constants/language';
import { CustomPagination } from '../../interfaces/index.types';
import Button from '../Button/Button';
import { buttonStyle, useStyles } from './styles';

import { useEffect } from 'react';

export default function Pagination({ data, rowsPerPage, onPageChange, currentPage }: CustomPagination & { currentPage?: number }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const maxPagesToShow = 5;
  const totalPages = Math.ceil(data.length / rowsPerPage);

  useEffect(() => {
    if (typeof currentPage === 'number' && currentPage !== page) {
      setPage(currentPage);
    }
  }, [currentPage]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    onPageChange(newPage);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let firstPage = Math.max(1, page - Math.floor((maxPagesToShow - 1) / 2));
    let lastPage = Math.min(totalPages, firstPage + maxPagesToShow - 1);

    if (firstPage !== 1) {
      pageNumbers.push(
        <Box key={1} className={classes.BoxPage}>
          <Button
            buttonStyle={buttonStyle}
            sxTextStyle={{ color: 'var(--Gray-600, #475467)', textAlign: 'center', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
            id="1"
            fullWidth={false}
            title="1"
            disable={0 === page}
            onClick={() => handleChangePage(0)}
          />
        </Box>
      );
    }

    if (firstPage > 2) {
      pageNumbers.push(
        <Box key="startEllipsis" className={classes.BoxPage} sx={{ padding: '8px' }}>
          <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} textAlign={'center'} color={'var(--Gray-600, #475467)'}>
            ...
          </Typography>
        </Box>
      );
    }

    for (let i = firstPage; i <= lastPage; i++) {
      pageNumbers.push(
        <Box key={i} className={classes.BoxPage}>
          <Button
            buttonStyle={buttonStyle}
            sxTextStyle={{ color: 'var(--Gray-600, #475467)', textAlign: 'center', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
            id={i.toString()}
            fullWidth={false}
            title={i.toString()}
            disable={i - 1 === page}
            onClick={() => handleChangePage(i - 1)}
          />
        </Box>
      );
    }

    if (lastPage < totalPages - 1) {
      pageNumbers.push(
        <Box key="endEllipsis" className={classes.BoxPage} sx={{ padding: '8px' }}>
          <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} textAlign={'center'} color={'var(--Gray-600, #475467)'}>
            ...
          </Typography>
        </Box>
      );
    }

    if (lastPage !== totalPages) {
      pageNumbers.push(
        <Box key={totalPages} className={classes.BoxPage}>
          <Button
            buttonStyle={buttonStyle}
            sxTextStyle={{ color: 'var(--Gray-600, #475467)', textAlign: 'center', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
            id={totalPages.toString()}
            fullWidth={false}
            title={totalPages.toString()}
            disable={totalPages - 1 === page}
            onClick={() => handleChangePage(totalPages - 1)}
          />
        </Box>
      );
    }

    return pageNumbers;
  };

  return (
    <Box className={classes.Box}>
      <Button
        id="login_button"
        fullWidth={false}
        title={t(LANG_TEXT.PREVIOUS)}
        buttonStyle={buttonStyle}
        sxTextStyle={{ color: 'var(--Gray-700, #344054)', fontWeight: 600, lineHeight: '20px' }}
        disable={page === 0}
        onClick={() => handleChangePage(page - 1)}
        startIcon={<ArrowBackIcon sx={{ color: 'rgba(52, 64, 84, 1)' }} />}
      />
      <Box className={classes.PageNumbers}>{renderPageNumbers()}</Box>
      <Button
        id="login_button"
        fullWidth={false}
        title={t(LANG_TEXT.NEXT)}
        buttonStyle={buttonStyle}
        sxTextStyle={{ color: 'var(--Gray-700, #344054)', fontWeight: 600, lineHeight: '20px' }}
        disable={page === totalPages - 1}
        onClick={() => handleChangePage(page + 1)}
        boxStyle={{
          display: 'flex',
          justifyContent: 'end',
        }}
        endIcon={<ArrowForwardIcon sx={{ color: 'rgba(52, 64, 84, 1)' }} />}
      />
    </Box>
  );
}
